import React, { useEffect, useState } from 'react';
import { useI18n } from '@/i18n';
import { 
  RefreshCw, 
  Layers, 
  Play, 
  Clock, 
  Edit2,
  Power,
  Activity,
  ExternalLink
} from 'lucide-react';
import { getWorkflows, getStats, toggleWorkflow, runWorkflow, type Workflow, type Execution } from '@/api/automation';
import { toast } from 'react-hot-toast';
import { useErrorToast } from '@/lib/notify';

const AutomationPage: React.FC = () => {
  const { t } = useI18n();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [workflowsData, statsData] = await Promise.all([
        getWorkflows(),
        getStats()
      ]);
      
      const workflowList = Array.isArray(workflowsData) ? workflowsData : (workflowsData as any).data || [];
      const executionList = Array.isArray(statsData) ? statsData : (statsData as any).data || [];
      
      setWorkflows(workflowList);
      setExecutions(executionList);
    } catch (err) {
      console.error('Failed to fetch n8n data:', err);
      setError('Could not connect to n8n API.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggle = async (workflow: Workflow) => {
    try {
      await toggleWorkflow(workflow.id, !workflow.active);
      toast.success(`Workflow ${!workflow.active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to toggle workflow');
    }
  };

  const handleRun = async (id: string) => {
    const toastId = toast.loading('Executing workflow...');
    try {
      await runWorkflow(id);
      toast.success('Workflow executed successfully', { id: toastId });
      // Wait a bit for execution to finish and refresh stats
      setTimeout(fetchData, 2000);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Execution failed (Check if Webhook exists)', { id: toastId });
    }
  };

  const handleOpenN8n = () => {
    window.open('/n8n/', '_blank');
  };

  const handleEditWorkflow = (id: string) => {
    window.open(`/n8n/workflow/${id}`, '_blank');
  };

  const getRelativeTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
      if (diffInSeconds < 60) return 'just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-center justify-between bg-white dark:bg-slate-900 px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-indigo-600 rounded-lg text-white">
            <Layers size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white uppercase tracking-tight">
              {t('automation.title')}
            </h1>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-1.5 text-gray-500 hover:text-indigo-600 rounded-lg bg-gray-50 dark:bg-slate-800 transition-all"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleOpenN8n}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-all"
          >
            <ExternalLink size={14} />
            <span>n8n Panel</span>
          </button>
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50">
            <h2 className="text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider">Workflows</h2>
            <span className="text-[10px] font-bold text-gray-400 bg-white dark:bg-slate-900 px-2 py-0.5 rounded border border-gray-100 dark:border-slate-800">
              {workflows.length} Total
            </span>
          </div>

          <div className="flex-1 overflow-auto max-h-[600px]">
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-white dark:bg-slate-900 z-10">
                <tr className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest border-b border-gray-100 dark:border-slate-800">
                  <th className="px-4 py-2">Workflow Name</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800/50">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3"><div className="h-3 bg-gray-100 dark:bg-slate-800 rounded w-32"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-100 dark:bg-slate-800 rounded-full w-16"></div></td>
                      <td className="px-4 py-3 text-right"><div className="h-6 bg-gray-100 dark:bg-slate-800 rounded w-16 ml-auto"></div></td>
                    </tr>
                  ))
                ) : workflows.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-12 text-center text-xs text-gray-400 italic">No workflows found in n8n</td></tr>
                ) : (
                  workflows.map((wf) => (
                    <tr key={wf.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/30 transition-colors group">
                      <td className="px-4 py-2.5">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate max-w-[250px]">{wf.name}</span>
                          <span className="text-[9px] text-gray-400 uppercase">ID: {wf.id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <button 
                          onClick={() => handleToggle(wf)}
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-bold transition-all border ${
                            wf.active 
                              ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800/50' 
                              : 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700'
                          }`}
                        >
                          <Power size={10} className="mr-1" />
                          {wf.active ? 'ACTIVE' : 'INACTIVE'}
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => handleEditWorkflow(wf.id)}
                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-all"
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                             onClick={() => handleRun(wf.id)}
                             className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all"
                             title="Manual Run (Requires Webhook node)"
                          >
                             <Play size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-800/50 flex items-center space-x-2">
            <Activity size={16} className="text-gray-400" />
            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider">Activity Log</h2>
          </div>
          
          <div className="flex-1 p-4 overflow-auto max-h-[600px]">
            <div className="space-y-4">
              {loading ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-10 bg-gray-50 dark:bg-slate-800 rounded-lg"></div>
                  <div className="h-10 bg-gray-50 dark:bg-slate-800 rounded-lg"></div>
                </div>
              ) : executions.length === 0 ? (
                <p className="text-center text-[11px] text-gray-400 py-4 italic">No recent activity</p>
              ) : (
                executions.map((exec) => {
                  const workflow = workflows.find(w => w.id === exec.workflowId);
                  const isSuccess = exec.status === 'success';
                  return (
                    <div key={exec.id} className="group relative pl-4 border-l-2 border-gray-100 dark:border-slate-800 hover:border-indigo-500 transition-all">
                      <div className={`absolute -left-[5px] top-1.5 w-2 h-2 rounded-full ${isSuccess ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]' : 'bg-red-500'}`}></div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-800 dark:text-slate-200 truncate">{workflow?.name || 'Unknown Workflow'}</span>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-[9px] font-bold uppercase tracking-tight ${isSuccess ? 'text-green-600' : 'text-red-500'}`}>
                            {exec.status}
                          </span>
                          <span className="text-[9px] text-gray-400 font-medium flex items-center">
                            <Clock size={8} className="mr-1" />
                            {getRelativeTime(exec.startedAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            <button
               onClick={handleOpenN8n}
               className="w-full mt-6 py-2 border border-gray-100 dark:border-slate-800 text-gray-400 dark:text-slate-500 text-[10px] font-bold rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors uppercase tracking-widest"
            >
              System Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationPage;
